import { useEffect, useState } from 'react'
import { useI18n } from '../lib/i18n'

/**
 * Aviso discreto de instalação.
 *
 * O app já é instalável (manifest.webmanifest + sw.js, doc 06 §7), mas nada
 * na interface oferecia isso — a pessoa só descobria vasculhando o menu do
 * navegador. Uma tira de uma linha, não modal: aparece depois que o áudio já
 * conectou (a pessoa já está operando, não é a primeira coisa que vê), some
 * ao dispensar, e só volta a incomodar depois de duas semanas — e nunca mais,
 * se a instalação de fato aconteceu.
 */

const CHAVE_DISPENSA = 'radioamador:pwa-dispensa'
const CHAVE_INSTALADO = 'radioamador:pwa-instalado'
const ESPERA_MS = 15_000
const REAPARECE_MS = 14 * 24 * 60 * 60 * 1000

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

function jaInstalado(): boolean {
  try {
    if (localStorage.getItem(CHAVE_INSTALADO) === '1') return true
  } catch {
    // segue para a checagem de display-mode
  }
  const standalone =
    window.matchMedia('(display-mode: standalone)').matches ||
    (navigator as Navigator & { standalone?: boolean }).standalone === true
  return standalone
}

function dispensadoRecentemente(): boolean {
  try {
    const v = localStorage.getItem(CHAVE_DISPENSA)
    if (!v) return false
    return Date.now() - Number(v) < REAPARECE_MS
  } catch {
    return false
  }
}

const iOS = /iphone|ipad|ipod/i.test(navigator.userAgent)

export function InstalarApp({ mediaState }: { mediaState: RTCPeerConnectionState | null }) {
  const { t } = useI18n()
  const [prompt, setPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [pronto, setPronto] = useState(false)
  const [visivel, setVisivel] = useState(false)

  useEffect(() => {
    if (jaInstalado()) return
    const aoOferecer = (e: Event) => {
      e.preventDefault()
      setPrompt(e as BeforeInstallPromptEvent)
    }
    const aoInstalar = () => {
      try {
        localStorage.setItem(CHAVE_INSTALADO, '1')
      } catch {
        // já não tem mais o que fazer além de esconder nesta sessão
      }
      setVisivel(false)
    }
    window.addEventListener('beforeinstallprompt', aoOferecer)
    window.addEventListener('appinstalled', aoInstalar)
    return () => {
      window.removeEventListener('beforeinstallprompt', aoOferecer)
      window.removeEventListener('appinstalled', aoInstalar)
    }
  }, [])

  // Só depois de operando de verdade, e nunca antes do primeiro áudio
  // conectado nesta sessão — não é a primeira coisa que a pessoa vê.
  useEffect(() => {
    if (mediaState !== 'connected' || pronto) return
    const t = setTimeout(() => setPronto(true), ESPERA_MS)
    return () => clearTimeout(t)
  }, [mediaState, pronto])

  useEffect(() => {
    if (!pronto) return
    if (jaInstalado() || dispensadoRecentemente()) return
    if (!iOS && !prompt) return // Android/desktop sem o evento: nada a oferecer
    setVisivel(true)
  }, [pronto, prompt])

  if (!visivel) return null

  const dispensar = () => {
    try {
      localStorage.setItem(CHAVE_DISPENSA, String(Date.now()))
    } catch {
      // esconde só nesta sessão
    }
    setVisivel(false)
  }

  const instalar = async () => {
    if (!prompt) return
    await prompt.prompt()
    const { outcome } = await prompt.userChoice
    if (outcome === 'accepted') {
      try {
        localStorage.setItem(CHAVE_INSTALADO, '1')
      } catch {
        // segue: o evento appinstalled também tenta gravar isto
      }
    }
    setVisivel(false)
  }

  return (
    <div className="instalar-app">
      <span>{iOS ? t.instalarApp.iosInstrucao : t.instalarApp.outroInstrucao}</span>
      {!iOS && (
        <button className="link" onClick={() => void instalar()}>
          {t.instalarApp.instalar}
        </button>
      )}
      <button className="link" onClick={dispensar} aria-label={t.instalarApp.dispensar}>
        ×
      </button>
    </div>
  )
}
